// ============================================================================
// authFallback.js — az átirányításos bejelentkezés NÉMA kudarcának
// felismerése, és automatikus átállás felugró ablakra.
//
// A PROBLÉMA:
//
// A Firebase átirányításos bejelentkezése (signInWithRedirect) olyan
// tárolási csatornát használ, amit a szigorúan tároláselválasztó böngészők
// (Safari/WebKit, Firefox) elvághatnak. Ilyenkor a felhasználó végigmegy a
// Google-bejelentkezésen, visszatér az alkalmazásba - és egyszerűen nem
// történik semmi: nincs hibaüzenet, mert a Firebase szemszögéből a böngésző
// "csak nem adott vissza semmit". A felhasználó a belépő képernyőn ragad, és
// hiába próbálja újra, mindig ugyanoda jut.
//
// A FELISMERÉS:
//
// Átirányítás előtt jelet teszünk le (sessionStorage - a munkamenethez
// kötve, tehát a visszatéréskor még megvan). Ha az alkalmazás elindul, a jel
// ott van, de bejelentkezett felhasználó NINCS, akkor pontosan ez a néma
// kudarc történt. Ilyenkor az eszközre megjegyezzük, hogy itt a felugró
// ablakos utat kell használni (localStorage), és onnantól az megy.
//
// Miért nem eleve felugró ablak? Mert telepített (standalone) PWA-ban a
// rendszer nem nyit új ablakot - ott az átirányítás az egyetlen működő út.
// ============================================================================

const REDIRECT_MARKER = "lineage0_auth_redirect_started";
const PREFER_POPUP_KEY = "lineage0_auth_prefer_popup";

// A jel csak rövid ideig érvényes: egy napokkal későbbi indulás nem
// számít "most tértünk vissza a bejelentkezésből" esetnek.
const MARKER_MAX_AGE_MS = 10 * 60 * 1000;

function safeGet(store, key) {
  try {
    return store ? store.getItem(key) : null;
  } catch (e) {
    return null; // privát böngészés / letiltott tárolás
  }
}
function safeSet(store, key, value) {
  try {
    if (store) store.setItem(key, value);
  } catch (e) {
    // nem kritikus
  }
}
function safeRemove(store, key) {
  try {
    if (store) store.removeItem(key);
  } catch (e) {
    // nem kritikus
  }
}

/** Átirányítás előtt: lerakjuk a jelet. */
export function markRedirectStarted(session = defaultSession()) {
  safeSet(session, REDIRECT_MARKER, String(Date.now()));
}

/** Sikeres bejelentkezéskor: a jel már nem kell. */
export function clearRedirectMarker(session = defaultSession()) {
  safeRemove(session, REDIRECT_MARKER);
}

/**
 * Néma kudarc történt-e? Igaz, ha nemrég indult átirányítás, de nincs
 * bejelentkezett felhasználó.
 *
 * @param {boolean} vanFelhasznalo
 * @param {number} [most]
 */
export function redirectSilentlyFailed(vanFelhasznalo, most = Date.now(), session = defaultSession()) {
  const jel = safeGet(session, REDIRECT_MARKER);
  if (!jel) return false;
  if (vanFelhasznalo) return false;
  const kor = most - Number(jel);
  return Number.isFinite(kor) && kor >= 0 && kor <= MARKER_MAX_AGE_MS;
}

/** Az eszközön a felugró ablakos utat kell használni? */
export function shouldUsePopup(local = defaultLocal()) {
  return safeGet(local, PREFER_POPUP_KEY) === "1";
}

/** Megjegyezzük, hogy ezen az eszközön a felugró ablak az út. */
export function rememberPopupNeeded(local = defaultLocal()) {
  safeSet(local, PREFER_POPUP_KEY, "1");
}

/** A felugró ablakos preferencia törlése (pl. ha mégis működik a redirect). */
export function forgetPopupNeeded(local = defaultLocal()) {
  safeRemove(local, PREFER_POPUP_KEY);
}

function defaultSession() {
  return typeof sessionStorage !== "undefined" ? sessionStorage : null;
}
function defaultLocal() {
  return typeof localStorage !== "undefined" ? localStorage : null;
}


// ---------------------------------------------------------------------------
// A bejelentkezési út kiválasztása (2026-10-10, valós hiba: Androidon a Google utáni visszatérésnél a Firebase
// "Unable to process request due to missing initial state" oldalát kaptuk)
//
// Az átirányításos bejelentkezés az indulás előtt elmenti az állapotát a lap tárolójába, és a Google utáni
// visszatéréskor UGYANABBAN a lapban várja vissza. Androidon a visszatérő hivatkozás más lapon vagy ablakban is
// megnyílhat (pl. a telepített alkalmazás - a WebAPK - átveszi a saját tartományának hivatkozásait, vagy a böngésző új
// lapot nyit), ott az állapot nincs meg, és a hitelesítés elbukik. A felugró ablakos út ezt kikerüli: az egész
// folyamat egy ablakban zajlik, az eredmény pedig üzenetben tér vissza a megnyitó laphoz.
//
//   Android (böngészőben és telepített alkalmazásban is) -> felugró ablak (ha letiltották, marad az átirányítás);
//   iPhone / iPad telepített módban                       -> átirányítás (ott a rendszer nem nyit új ablakot);
//   egyébként: ha az eszközön már láttuk az átirányítás néma kudarcát, vagy WebKit -> felugró ablak;
//              különben átirányítás (asztali Chrome / Edge / Firefox).
// ---------------------------------------------------------------------------
export function isAndroid(userAgent) {
  const ua = typeof userAgent === "string"
    ? userAgent
    : (typeof navigator !== "undefined" && navigator && typeof navigator.userAgent === "string" ? navigator.userAgent : "");
  return /Android/i.test(ua);
}

/** @returns {"popup"|"redirect"} */
export function bejelentkezesiUt({ standalone = false, android = false, webkit = false, popupKell = false } = {}) {
  if (android) return "popup";
  if (standalone) return "redirect";
  // v213 (valós hiba: egy román felhasználó asztali gépen elsőre L3 hibát kapott): a felugró ablak nem függ a lap
  // tárolójától, amit a Chrome / Edge / Firefox is elvághat - ezért telepített módon kívül mindenhol ez az első út.
  // (Ha a böngésző letiltja, a handleSignIn automatikusan átirányításra esik vissza.) A popupKell / webkit
  // paraméterek megmaradnak a hívási felület kompatibilitása miatt.
  void popupKell; void webkit;
  return "popup";
}
